import React, {forwardRef, useContext, useLayoutEffect, useState} from 'react'
import ProjectsListManager from "../../../../core/gd/manager/projects-list-manager"
import NavigationMenuItemProject from "./menu-item-project"
import ProjectsReorderList from "../../sections/projects/projects-reorder-list"
import ArrayUtils from "../../../../core/gd/utils/array"
import ProjectSession from "../../../../core/model2/project/project-session"
import NavigationMenu_ProjectsInlineAdd from "../../sections/projects/tree/inline-add"
import {ProjectSystemType} from "../../../../core/model2/project/types"
import {BUS_APP_LAUNCH, PROJECT_ITEM_VIEW_CLOSE} from "../../../../core/gd/app-manager/@event"
import {NavigationMenuInlineAddParams, NavigationMenuInlineAddType} from "../menu-add-with-popover/@types"
import {useForceUpdate} from "../../../../core/gd/utils/dom-and-react/react-utils"
import {SpaceProto} from "../../../../core/model2/space/space-proto"
import {MenuProjectsDndContext} from "../../sections/projects/dnd/dnd-root"
import MenuProject_DndItem from "../../sections/projects/dnd/menu-project-dnd-item"

type Props = {
    level                       : number
    localSettings               : any
    expandedProjectsSettingName : string
    project                     : ProjectSession
    space                       : SpaceProto
    dndPreview?                 : boolean

    // only set inside a MenuProjects_DndRoot - starred/pins render these rows without drag and drop
    parentId?                   : ID
    siblings?                   : ProjectSession[]
}

// const debug = "NavigationMenuItemProjectWithReorder"
const debug = false

const NavigationMenu_ItemProjectWithReorder = forwardRef((props: Props, ref: any) => {

    const [inlineAddParams, setInlineAddParams] = useState<any>(null)   // parameters for inline add
    const [processingAdd, setProcessingAdd] = useState<any>(false)      // is true when inline add processing is in progress and changes are awaited

    const forceUpdate = useForceUpdate()
    const dndManager  = useContext(MenuProjectsDndContext)

    useLayoutEffect(() => {
        const unsubscribeLaunch = gd.bus.subscribeNoContext(BUS_APP_LAUNCH, onLaunchApp)
        const unsubscribeItemViewClose = gd.bus.subscribeNoContext(PROJECT_ITEM_VIEW_CLOSE, onItemViewClose) // for apps like project report view

        return () => {
            unsubscribeLaunch()
            unsubscribeItemViewClose()
        }
    }, [])

    useLayoutEffect(() => {
        // if we are waiting for project to be added and treeChildren is modified then hide inline add before rerender
        if (processingAdd) {
            setInlineAddParams(null)
            setProcessingAdd(false)
        }
    }, [props.project.treeChildren.length])

    const onLaunchApp = (data: any) => {
        debug && console.log(debug, 'onLaunchApp() data', data)

        const projectId = data.params?.projectId || data.params?.customViewData?.parentProjectId
        if (projectId) {
            const projectParentsIds = gd.tree.findProjectParents(gd.tree.getProject(projectId)).map((p:any)=>p.id)
            if (projectParentsIds?.includes(props.project.id)) {
                const newExpandedItems = _.union(props.localSettings[props.expandedProjectsSettingName].slice(), projectParentsIds)

                debug && console.log(debug, 'onLaunchApp', projectId, newExpandedItems)

                props.localSettings[props.expandedProjectsSettingName] = newExpandedItems
                props.localSettings.save()
            }
        }

        forceUpdate()
    }

    const onItemViewClose = (project: ProjectSession) => {
        forceUpdate()
    }

    const onMenuItemHandleClick = (id: ID, e: any) => {
        e.stopPropagation()

        const newExpandedItems = props.localSettings[props.expandedProjectsSettingName].slice()
        const index = newExpandedItems.indexOf(id)

        if (index !== -1) {
            newExpandedItems.splice(index, 1)
        } else {
            newExpandedItems.push(id)
        }

        props.localSettings[props.expandedProjectsSettingName] = newExpandedItems;
        props.localSettings.save()

        forceUpdate()
    }

    const onAddInline = (inlineType: NavigationMenuInlineAddType, parentId: ID, params: NavigationMenuInlineAddParams) => {
        debug && console.log(debug, 'onAddInline', inlineType, parentId, params)

        const expandedProjectIds = props.localSettings[props.expandedProjectsSettingName]
        props.localSettings[props.expandedProjectsSettingName] = ArrayUtils.addValueToArray(expandedProjectIds, parentId)
        props.localSettings.save()

        setInlineAddParams({
            inlineType  : inlineType,
            addParentId : parentId,
            params      : params, // for folder, page and embed
        })
    }

    const onInlineAdded = (isAdded: boolean) => {
        debug && console.log(debug, 'onInlineAdded', isAdded)

        // if nothing added and project has no children then collapse it
        if (!isAdded && _.isEmpty(ProjectsListManager.getChildProjects(null, inlineAddParams?.addParentId))) {
            const expandedProjectIds = props.localSettings[props.expandedProjectsSettingName]
            props.localSettings[props.expandedProjectsSettingName] = ArrayUtils.removeValueFromArray(expandedProjectIds, inlineAddParams?.addParentId)
            props.localSettings.save()
        }

        if (isAdded) {
            setProcessingAdd(true)
        } else {
            setInlineAddParams(null)
        }
    }

    const renderListItems = (level: number, parent: any | null, submenu: any[]) => {
        debug && console.log(debug, "renderListItems() props.project=",props.project," level=", level, "parent=", parent, "submenu=", submenu )

        let items : ProjectSession[] = []
        if (!parent) {
            debug && console.log(debug, "    no parent. props.project = ", props.project)
            items.push(props.project)
        } else {
            items = props.project.treeChildren
        }

        if (items && items.length > 0) {
            items.map((listItem: ProjectSession)=> {

                const isExpanded : boolean = props.localSettings[props.expandedProjectsSettingName].includes(listItem.id)  && listItem.hasAccessibleChildren
                const showChildProjects = isExpanded && listItem.treeChildren && listItem.hasAccessibleChildren
                const showInlineAdd = inlineAddParams?.addParentId === listItem.id
                const fragment = gd.router.history.fragment || ''
                const isSelected = _.startsWith(fragment, 'p/') && fragment.includes(listItem.id)

                // Esli eto report bez parent -> ne pokazivaem
                if (listItem.systemType === ProjectSystemType.REPORT_VIEW && !listItem.parentId) return

                const row = (
                    <NavigationMenuItemProject
                        key                     = {listItem.id}
                        ref                     = {ref}
                        project                 = {listItem}
                        isSelected              = {isSelected}
                        isExpanded              = {isExpanded}
                        level                   = {level}
                        colorized               = {props.space.projectsColorize}
                        onMenuItemHandleClick   = {onMenuItemHandleClick}
                        onAddInline             = {onAddInline}
                    />
                )

                // wraps the row alone, never the subtree below it - the drop zones are measured
                // against this element, so it has to be exactly one row tall
                submenu.push(dndManager
                    ?
                    <MenuProject_DndItem
                        key             = {listItem.id}
                        dndManager      = {dndManager}
                        project         = {listItem}
                        space           = {props.space}
                        localSettings   = {props.localSettings}
                        isExpanded      = {isExpanded}
                        parentId        = {props.parentId!}
                        siblings        = {props.siblings!}
                    >
                        {row}
                    </MenuProject_DndItem>
                    : row)

                if (showChildProjects) {
                    submenu.push(
                        <ProjectsReorderList
                            key             = {'list_'+listItem.id}
                            space           = {props.space}
                            level           = {level+1}
                            parentId        = {listItem.id}
                            projects        = {listItem.treeChildren}
                            localSettings   = {props.localSettings}
                        />
                    )
                }

                if (showInlineAdd) {
                    submenu.push(
                        <NavigationMenu_ProjectsInlineAdd
                            key         = {'add_{0}'.f(listItem.id)}
                            type        = {inlineAddParams.inlineType}
                            level       = {level+1}
                            parentId    = {inlineAddParams.addParentId}
                            params      = {inlineAddParams.params}
                            callback    = {onInlineAdded}
                        />
                    )
                }
            })
        }
    }

    const submenu: any[] = []
    renderListItems(props.level, null, submenu)

    const classes = Classes("root-pinned-project-container with-reorder", {
        'dnd-preview': props.dndPreview,
    })

    return (
        <div className={classes}>
            {submenu}
        </div>
    )
})

export default NavigationMenu_ItemProjectWithReorder
